import { EFinancingProgram } from '@if/core/types'
import {
  EasyCareComputeRequestDto,
  EasyCareComputeResultDto,
  EasyCareParentLoanScheduleDto,
  EasyCareWorksheet,
  EasyCareWorksheetDecisionDTO,
  EditEasyCareWorksheetDto,
} from '@src/data/types/EasyCareWorksheetSchema'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'

const worksheetUrl = '/Applications/easycare/{creditApplicationId}/Worksheet'

const buildUrl = (creditApplicationId: string) => worksheetUrl.replace('{creditApplicationId}', creditApplicationId)

export async function createEasyCareWorksheet(data: EditEasyCareWorksheetDto): Promise<EasyCareWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.post<EasyCareWorksheet>(buildUrl(data.creditApplicationId), data)
  return response.data
}

export async function updateEasyCareWorksheet(data: EditEasyCareWorksheetDto): Promise<EasyCareWorksheet> {
  const apiClient = getApiClient()
  const response = await apiClient.put<EasyCareWorksheet>(buildUrl(data.creditApplicationId), data)
  return response.data
}

function useSaveEasyCareWorksheet(
  mutationFn: (data: EditEasyCareWorksheetDto) => Promise<EasyCareWorksheet>,
): [MutationFunction<EasyCareWorksheet, EditEasyCareWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn,
    onSuccess: (savedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(savedWorksheet.creditApplicationId, EFinancingProgram.EasyCareProtectionPlan)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: savedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useCreateEasyCareWorksheet(): [MutationFunction<EasyCareWorksheet, EditEasyCareWorksheetDto>, boolean] {
  return useSaveEasyCareWorksheet(createEasyCareWorksheet)
}

export function useUpdateEasyCareWorksheet(): [MutationFunction<EasyCareWorksheet, EditEasyCareWorksheetDto>, boolean] {
  return useSaveEasyCareWorksheet(updateEasyCareWorksheet)
}

export async function computeEasyCare(dto: EasyCareComputeRequestDto): Promise<EasyCareComputeResultDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post<EasyCareComputeResultDto>(
    `/Applications/${EFinancingProgram.EasyCareProtectionPlan}/Calculator/Compute`,
    dto,
  )
  return response.data
}

export async function updateEasyCareWorksheetDecision(
  data: EasyCareWorksheetDecisionDTO,
): Promise<EasyCareWorksheet> {
  const apiClient = getApiClient()
  const url = `/Applications/${EFinancingProgram.EasyCareProtectionPlan}/${data.creditApplicationId}/Worksheet/Decision`
  const response = await apiClient.put<EasyCareWorksheet>(url, data)
  return response.data
}

export function useUpdateEasyCareWorksheetDecision(): [
  MutationFunction<EasyCareWorksheet, EasyCareWorksheetDecisionDTO>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateEasyCareWorksheetDecision,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(
        updatedWorksheet.creditApplicationId,
        EFinancingProgram.EasyCareProtectionPlan,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export async function getEasyCareParentLoanSchedule(
  creditApplicationId: string,
): Promise<EasyCareParentLoanScheduleDto> {
  const apiClient = getApiClient()
  const response = await apiClient.get<EasyCareParentLoanScheduleDto>(
    `${buildUrl(creditApplicationId)}/ParentLoanSchedule`,
  )
  return response.data
}

export function useEasyCareParentLoanSchedule(
  creditApplicationId: string,
): [EasyCareParentLoanScheduleDto | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: ['easyCareParentLoanSchedule', creditApplicationId],
    queryFn: () => getEasyCareParentLoanSchedule(creditApplicationId),
    enabled: !!creditApplicationId,
  })

  return [data ?? null, isFetching]
}

export function useComputeEasyCare(
  dto: EasyCareComputeRequestDto | undefined,
): [EasyCareComputeResultDto | null, boolean, boolean] {
  const { data, isFetching, isError } = useQuery({
    queryKey: ['easyCareCompute', dto],
    queryFn: () => computeEasyCare(dto!),
    enabled: !!dto && (dto.includeVehicleAssistance || dto.includeLoanProtection),
  })

  return [data ?? null, isFetching, isError]
}
